import { useCallback, useMemo, useState } from 'react';
import { isAfter, isDate, isWithinInterval } from 'date-fns';
import { ReportingPeriodType } from '@/@types/client-api';
import { ComplianceFormFields } from '@credit-tracker/Forms/ComplianceForm';
import { ComplianceFieldErrors, ComplianceFieldNames } from '@credit-tracker/util/constants';
import { getDateFromString } from '@credit-tracker/util/trackerDateHelpers';
import { parseDate } from '@/utils/helpers';

export type ComplianceValidationProps = {
  compliancePeriod: ComplianceFormFields;
  reportingPeriod?: ReportingPeriodType;
  selectedCompliancePeriod?: string | null;
};

const useComplianceFormValidation = ({
  compliancePeriod,
  reportingPeriod,
  selectedCompliancePeriod
}: ComplianceValidationProps) => {
  const [fieldsWithErrors, setFieldsWithErrors] = useState<ComplianceFieldErrors>();

  const admitYear = useMemo(() => {
    const date = new Date(compliancePeriod?.admitDate ?? '');
    if (isDate(date)) return date.getFullYear();
  }, [compliancePeriod?.admitDate]);

  const intervalHasTodaysDate = useCallback(() => {
    const today = new Date().setHours(0, 0, 0, 0);
    const start = getDateFromString(compliancePeriod.startDate);
    const end = getDateFromString(compliancePeriod.endDate);
    return (
      !!start &&
      !!end &&
      isWithinInterval(today, {
        start: start,
        end: end
      })
    );
  }, [compliancePeriod.endDate, compliancePeriod.startDate]);

  const handleValidation = useCallback(
    (isNewlyAdmitted: boolean): boolean => {
      const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;
      const errorsFound: ComplianceFieldErrors = {};

      if (
        (reportingPeriod === ReportingPeriodType._1 ||
          reportingPeriod === ReportingPeriodType._5) &&
        !compliancePeriod.complianceGroup
      ) {
        errorsFound[ComplianceFieldNames.COMPLIANCE_GROUP] = {
          message: 'Enter a Compliance Group',
          showError: true,
          match: (value: string): boolean => !!value,
          forceMatch: true
        };
      }

      if (
        (reportingPeriod === ReportingPeriodType._2 && !isNewlyAdmitted) ||
        reportingPeriod === ReportingPeriodType._3
      ) {
        if (!compliancePeriod?.birthMonth) {
          errorsFound[ComplianceFieldNames.BIRTH_MONTH] = {
            message: 'Enter a Birth Month',
            showError: true,
            match: (value: string): boolean => !!value,
            forceMatch: true
          };
        }
        if (!compliancePeriod?.birthDay) {
          errorsFound[ComplianceFieldNames.BIRTH_DAY] = {
            message: 'Enter a Birth Day',
            showError: true,
            match: (value: string): boolean => !!value,
            forceMatch: true
          };
        }
      }

      if (
        ((reportingPeriod === ReportingPeriodType._2 && isNewlyAdmitted) ||
          reportingPeriod === ReportingPeriodType._5) &&
        (!compliancePeriod.admitDate || !parseDate(compliancePeriod.admitDate))
      ) {
        errorsFound[ComplianceFieldNames.ADMIT_DATE] = {
          message: 'Enter an admission year',
          showError: true,
          match: (value: string): boolean => !!value,
          forceMatch: true
        };
      }

      if (reportingPeriod === ReportingPeriodType._2 && !isNewlyAdmitted && !admitYear) {
        errorsFound[ComplianceFieldNames.ADMIT_YEAR] = {
          message: 'Enter year admitted',
          showError: true,
          match: (value: string): boolean => !!value,
          forceMatch: true
        };
      }

      if (!compliancePeriod.startDate || !parseDate(compliancePeriod.startDate)) {
        errorsFound[ComplianceFieldNames.START_DATE] = {
          message: 'Please enter a valid start date.',
          showError: true,
          match: (value: string): boolean => !!value,
          forceMatch: true
        };
      }

      if (!compliancePeriod.endDate || !parseDate(compliancePeriod.endDate)) {
        errorsFound[ComplianceFieldNames.END_DATE] = {
          message: 'Please enter a valid end date.',
          showError: true,
          match: (value: string): boolean => !!value,
          forceMatch: true
        };
      }

      if (selectedCompliancePeriod === 'current' && !isNewlyAdmitted && !intervalHasTodaysDate()) {
        errorsFound[ComplianceFieldNames.DATE_INTERVAL] = {
          message:
            'The current period must contain today’s date. If you are trying to access a previous period, please enter the current period dates, and then enter or navigate to the previous period.',
          showError: true,
          match: (value: string): boolean => !!value,
          forceMatch: true
        };
      }

      if (
        compliancePeriod.startDate &&
        compliancePeriod.endDate &&
        isAfter(new Date(compliancePeriod.startDate), new Date(compliancePeriod.endDate))
      ) {
        errorsFound[ComplianceFieldNames.DATE_INTERVAL] = {
          message: 'Please enter valid date interval.',
          showError: true,
          match: (value: string): boolean => !!value,
          forceMatch: true
        };
      }

      if (
        (compliancePeriod.startDate && !regex.test(compliancePeriod.startDate)) ||
        (compliancePeriod.endDate && !regex.test(compliancePeriod.endDate))
      ) {
        return false;
      }

      if (Object.keys(errorsFound).length === 0) {
        return true;
      } else {
        setFieldsWithErrors(errorsFound);
        return false;
      }
    },
    [
      admitYear,
      compliancePeriod.admitDate,
      compliancePeriod?.birthDay,
      compliancePeriod?.birthMonth,
      compliancePeriod.complianceGroup,
      compliancePeriod.endDate,
      compliancePeriod.startDate,
      intervalHasTodaysDate,
      reportingPeriod,
      selectedCompliancePeriod
    ]
  );

  return {
    fieldsWithErrors,
    setFieldsWithErrors,
    handleValidation
  };
};

export default useComplianceFormValidation;
